import React, { useState } from 'react'
import { apis } from '@modules/apiClients'
import { UserService } from '@duck/api-definitions'
import { toast } from 'sonner'
import { StandardLoader } from '../StandardLoader'
import { useTar } from '@/modules'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@duck/ui'

interface ForgotPasswordDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  initialEmail?: string
}

export const ForgotPasswordDialog = ({
  open,
  onOpenChange,
  initialEmail = ''
}: ForgotPasswordDialogProps) => {
  const [email, setEmail] = useState(initialEmail)
  const [isLoading, setIsLoading] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const { t } = useTar()

  const handleRequestReset = async () => {
    if (!email || !email.includes('@')) {
      toast.error(t('auth.enterValidEmail'))
      return
    }

    try {
      setIsLoading(true)

      const forgotPasswordRequest: UserService.ForgotPasswordRequest = { email }
      await apis.userService.authForgotPasswordPost(forgotPasswordRequest)

      setIsSuccess(true)
      toast.success(t('auth.passwordResetEmailSent'))

    } catch (error: any) {
      console.error('Password reset request failed:', error)
      toast.error(t('auth.passwordResetRequestFailed'))
    } finally {
      setIsLoading(false)
    }
  }

  const handleClose = () => {
    if (!isLoading) {
      setEmail('')
      setIsSuccess(false)
      onOpenChange(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className="w-full h-full sm:w-auto sm:h-auto sm:max-h-[90vh] sm:max-w-md rounded-none sm:rounded-lg sm:min-w-[480px]"
        onPointerDownOutside={(e) => isLoading && e.preventDefault()}
        onEscapeKeyDown={(e) => isLoading && e.preventDefault()}
        onInteractOutside={(e) => isLoading && e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="text-lg sm:text-xl">
            {t('auth.forgotPasswordTitle')}
          </DialogTitle>
        </DialogHeader>

        {isSuccess ? (
          <div className="space-y-4">
            <div className="bg-success/10 border border-success/20 rounded-lg p-4">
              <div className="flex items-center mb-3">
                <svg className="w-5 h-5 text-success me-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <h4 className="text-sm font-medium text-success">
                  {t('auth.passwordResetEmailSent')}
                </h4>
              </div>
              <p className="text-sm text-foreground mt-1">
                {t('auth.passwordResetEmailInstructions', { email })}
              </p>
            </div>
            <button
              onClick={handleClose}
              className="w-full bg-accent hover:bg-accent/90 text-white font-medium py-2 px-4 rounded-lg transition-colors"
            >
              {t('common.close')}
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              {t('auth.forgotPasswordDescription')}
            </p>
            <div>
              <label htmlFor="forgot-password-email" className="block text-sm font-medium text-foreground mb-2">
                {t('auth.emailAddress')}
              </label>
              <input
                id="forgot-password-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('auth.emailPlaceholder')}
                disabled={isLoading}
                autoComplete="email"
                className={`w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground
                  placeholder-muted focus:outline-none focus:ring-2 focus:ring-ring 
                  focus:border-accent transition-colors
                  ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !isLoading && email.includes('@')) {
                    handleRequestReset()
                  }
                }}
              />
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleRequestReset}
                disabled={!email.includes('@') || isLoading}
                className={`flex-1 px-4 py-2 rounded-lg font-medium
                  focus:outline-none focus:ring-2 focus:ring-ring 
                  transition-colors duration-200 flex items-center justify-center
                  ${!email.includes('@') || isLoading
                    ? 'bg-accent/50 text-white cursor-not-allowed opacity-75'
                    : 'bg-accent text-white hover:bg-accent/90'
                  }`}
              >
                {isLoading ? (
                  <>
                    <StandardLoader size="sm" inline />
                    <span className="ms-2">{t('common.loading')}</span>
                  </>
                ) : (
                  t('auth.sendResetLink')
                )}
              </button>
              <button
                onClick={handleClose}
                disabled={isLoading}
                className="flex-1 bg-muted hover:bg-muted/80 text-foreground font-medium py-2 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
